/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flow_0_0_2, getFullItemOne, getSelectedItemOne, getCurrentUser,  getTenantConfig } from '@flexus/core';
import { PggCreateClaim, TENANT_CONFIG_OVERRIDES } from './pgg-create-claim';
import {of, merge, filter, take, map, switchMap, tap, startWith, catchError } from 'rxjs';
import { Validators, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CustomValidators, generateRange } from '@flexus/utilities';
import gql from 'graphql-tag';
import { environment } from '../../../../../environments/environment';

/**
 * PggEditClaim
 * -----------
 * Handles reopening & editing draft claims created from the refactored
 * PggCreateClaim flow:
 * - state 8   = server draft
 * - state 169 = local/offline draft (persisted via pggService)
 *
 * Key design decisions:
 * 1.  Spreads PggCreateClaim to inherit bigFormToStoreMapper, events, guards, etc.
 * 2.  Starts at CustomerDetails (company already known from the draft).
 * 3.  valuesToBigForm:
 *     a) Synchronously sets `selected_company` + `tenant_document_config` from the
 *        *summary* item (getSelectedItemOne) so that any initFormFields that fire
 *        before the full item arrives still resolve the correct tenant config.
 *     b) Asynchronously populates all form groups & flat controls from the *full*
 *        item (getFullItemOne).
 * 4.  ClaimSubmissionSummary navs use pggService (not buiService) and offer
 *     "Update Draft" (keeps draft state, including 8/169) + "Submit" (transitions to state 138).
 */
// Validator lookup – mirrors the one in PggCreateClaim.CustomerDetails.initFormFields
const getValidators = (controlName: string): any[] => {
	const validatorMap: Record<string, any[]> = {
		first_name: [
			Validators.required, Validators.minLength(2),
			CustomValidators.hardMaxLength(140),
			CustomValidators.onlyAllowedASCII([32, 39, 44, 46, ...generateRange(65, 90, 1), ...generateRange(96, 122, 1)])
		],
		surname: [
			Validators.required, Validators.minLength(2),
			CustomValidators.hardMaxLength(140),
			CustomValidators.onlyAllowedASCII([32, 39, 44, 46, ...generateRange(65, 90, 1), ...generateRange(96, 122, 1)])
		],
		id_number: [
			Validators.required,
			CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
			CustomValidators.alphaNumericWithForwardSlash,
			Validators.minLength(6), CustomValidators.hardMaxLength(13)
		],
		contact_number: [
			Validators.required,
			CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
			CustomValidators.contact_number,
			Validators.minLength(10), CustomValidators.hardMaxLength(12)
		],
		cell_number: [
			Validators.required,
			CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
			CustomValidators.numeric,
			Validators.minLength(10), CustomValidators.hardMaxLength(12)
		],
		email: [Validators.required, Validators.email, CustomValidators.onlyAllowedASCII(generateRange(32, 126, 1))],
		preferred_com_method: [Validators.required],
		complex: [CustomValidators.hardMaxLength(64)],
		complex_unit_number: [CustomValidators.hardMaxLength(64)],
		street_address: [Validators.required, CustomValidators.hardMaxLength(64)],
		suburb: [Validators.required, CustomValidators.hardMaxLength(64)],
		city: [Validators.required, CustomValidators.hardMaxLength(64)],
		province: [Validators.required, CustomValidators.hardMaxLength(64)],
		area_code: [Validators.required, CustomValidators.hardMaxLength(64)],
		postal_code: [Validators.required, CustomValidators.hardMaxLength(64)]
	};
	return validatorMap[controlName] ?? [];
};

/**
 * Resolve a value from the server item by walking a dot-separated store path.
 * Accepts a single path string or an array of fallback paths.
 */
const resolveDraftValue = (itemOne: any, storePath: string | string[] | undefined, fallback: any = '') => {
	if (!storePath) return fallback;
	const paths = Array.isArray(storePath) ? storePath : [storePath];
	for (const path of paths) {
		const parts = path.split('.');
		let current: any = itemOne;
		for (const part of parts) {
			current = current?.[part];
			if (current == null) break;
		}
		if (current != null && current !== '') return current;
	}
	return fallback;
};

// get_claim can return singleton objects for fields we usually treat as arrays.
const asArray = <T = any>(value: T | T[] | null | undefined): T[] => {
	if (value == null) return [];
	return Array.isArray(value) ? value : [value];
};

const asBooleanFromApi = (value: any): boolean => {
	if (value === true || value === 1) return true;
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
		if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') return false;
	}
	return Number(value) === 1;
};

const toNumberOrNull = (value: any): number | null => {
	if (value == null || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const parseLocation = (location: any): { lat: number | null; lng: number | null } => {
	if (location && typeof location === 'object') {
		return {
			lat: toNumberOrNull(location?.lat),
			lng: toNumberOrNull(location?.long ?? location?.lng)
		};
	}
	if (typeof location === 'string' && location.includes(',')) {
		const [lat, lng] = location.split(',');
		return { lat: toNumberOrNull(lat?.trim()), lng: toNumberOrNull(lng?.trim()) };
	}
	return { lat: null, lng: null };
};

const normalizeAppointment = (appointment: any) => {
	if (!appointment) return null;
	const rangeStart = appointment?.range_start ?? null;
	const rangeEnd = appointment?.range_end ?? null;
	const iso = typeof rangeStart === 'string' ? rangeStart : '';
	const datePart = iso.includes('T') ? iso.split('T')[0] : null;
	const timePart = iso.includes('T') ? iso.split('T')[1]?.slice(0, 5) ?? null : null;

	return {
		requestedappointmentdate: appointment?.requestedappointmentdate ?? datePart ?? null,
		requestedappointmenttime: appointment?.requestedappointmenttime ?? timePart ?? null,
		appointment_type_id: appointment?.appointment_type_id ?? appointment?.appointment_type ?? null,
		from_time: appointment?.from_time ?? rangeStart ?? null,
		to_time: appointment?.to_time ?? rangeEnd ?? null
	};
};

const resolveTenantId = (item: any): number | null => {
	const candidate =
		item?.tenant_id ??
		item?.source_id ??
		item?.claim?.tenant_id ??
		item?.loan_information?.tenant_id ??
		null;
	const parsed = Number(candidate);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Edit flow requirement:
 * preferred_com_method is not relevant and must be hidden/ignored entirely.
 * Build a tenant config variant with that field removed.
 */
const getEditCustomerDetailConfig = (tenantId: number | null | undefined, metadata: any = null) => {
	const tenantConfig = getTenantConfig(tenantId, metadata, TENANT_CONFIG_OVERRIDES);
	const contactFields = (tenantConfig?.contactDetails?.fields ?? []).filter((field: any) => field?.controlName !== 'preferred_com_method');

	return {
		...tenantConfig,
		contactDetails: {
			...(tenantConfig?.contactDetails ?? {}),
			fields: contactFields
		}
	};
};

const editFlowErrorMessages = (() => {
	const rest = { ...((PggCreateClaim as any)?.flowErrorMessages ?? {}) };
	delete rest['preferred_com_method:required'];
	return rest;
})();

/**
 * Standalone directCall function that resolves the tenant config (including
 * required_documents) for the draft's tenant_id.  Extracted so it can be used
 * as a **node-level** serverCall – flow-level serverCalls only fire when the
 * manifest item has `itemType: 'flow'`, which this flow does not set.
 */
const resolveEditTenantConfig = (http: any, store: any, sq: any, bf: any) => {
	return store.select(getFullItemOne).pipe(
		filter(Boolean),
		take(1),
		switchMap((itemOne: any) => {
			const tenantId =
				itemOne?.tenant_id ??
				itemOne?.source_id ??
				itemOne?.claim?.tenant_id ??
				itemOne?.loan_information?.tenant_id;
			if (!tenantId) return of(null);

			return sq
				.queryObject(
					gql`
						{
							allInfo {
								config_options {
									general {
										tenant_config {
											id
											name
											logo_url
											logo_alt
											demand_creation
											analyse_files_with_ai
											required_documents {
												type
												label
												required
											}
										}
									}
								}
							}
						}
					`,
					store
				)
				.pipe(
					take(1),
					map((res: any) => {
						// Same resolution logic as CompanySelection.companyList
						let tenantConfigs: any[] = [];
						if (Array.isArray(res)) tenantConfigs = res;
						else if (res?.allInfo?.config_options?.general?.tenant_config)
							tenantConfigs = res.allInfo.config_options.general.tenant_config;
						else if (res?.config_options?.general?.tenant_config)
							tenantConfigs = res.config_options.general.tenant_config;
						else if (res?.general?.tenant_config)
							tenantConfigs = res.general.tenant_config;
						else if (res?.tenant_config)
							tenantConfigs = res.tenant_config;
						else if (res?.allInfo?.tenant_config)
							tenantConfigs = res.allInfo.tenant_config || [];

						if (!Array.isArray(tenantConfigs)) tenantConfigs = [];

						return tenantConfigs.find((c: any) => Number(c.id) === Number(tenantId)) ?? null;
					}),
					tap((config: any) => {
						if (!config) return;

						const companyId = Number(config.id);

						// Build the same shape CompanySelectionComponent produces
						const required_documents = (config.required_documents || []).map((doc: any) => ({
							type: String(doc.type || ''),
							label: String(doc.label || ''),
							required: Boolean(doc.required !== false),
							allowed_file_types: ['pdf', 'jpg', 'jpeg', 'png'],
							max_file_size_mb: 10,
							validation_rules: {}
						}));

						const tenantDocConfig = {
							company_id: companyId,
							company_name: String(config.name || ''),
							required_documents,
							metadata: {
								customer_detail_config: getEditCustomerDetailConfig(companyId, null)
							},
							demand_creation: Boolean(config.demand_creation === true),
							analyse_files_with_ai: config.analyse_files_with_ai === true
						};

						// Populate tenant_document_config (triggers SupportingDocumentUpload valueChanges)
						if (bf.bigForm.get('tenant_document_config')) {
							bf.bigForm.get('tenant_document_config').setValue(tenantDocConfig);
						} else {
							bf.bigForm.addControl('tenant_document_config', new UntypedFormControl(tenantDocConfig));
						}

						// Ensure selected_company is set (fixes voucher/store API calls)
						if (bf.bigForm.get('selected_company')) {
							bf.bigForm.get('selected_company').setValue(companyId);
						} else {
							bf.bigForm.addControl('selected_company', new UntypedFormControl(companyId));
						}

						// Cosmetic: company name & logo
						bf.bigForm.get('selected_company_name')?.setValue(String(config.name || ''));
						bf.bigForm.get('selected_company_logo')?.setValue(String(config.logo_url || ''));
					})
				);
		})
	);
};

// What Matters portal nav disabled – uncomment to re-enable the side panel button across edit-flow nodes
// const whatMattersNav = {
// 	text: 'What Matters',
// 	location: 'center' as const,
// 	linkType: 'portal' as const,
// 	portalData: {
// 		type: 'actionPanel' as const,
// 		paramFunc: (instance: any, mapper: any) => ({
// 			id: '1',
// 			icon: 'what-matters',
// 			command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
// 		})
// 	}
// };

export const PggEditClaim: Flow_0_0_2 = {
	// Inherit everything from PggCreateClaim (guards, mapper, events, etc.)
	...PggCreateClaim,

	id: 'edit_item_one',
	name: 'edit claim',

	header: {
		...PggCreateClaim.header,
		title: 'Edit Draft'
	},
	flowErrorMessages: editFlowErrorMessages,

	// Company is already selected on the draft – skip CompanySelection
	startNode: 'CustomerDetails',

	// ---------------------------------------------------------------------------
	// serverCalls – inherit PggCreateClaim calls + add tenant config resolution
	// ---------------------------------------------------------------------------
	serverCalls: {
		...PggCreateClaim.serverCalls,

		/**
		 * Fetches the full tenant config (including required_documents) for the
		 * draft's tenant_id.  Delegates to resolveEditTenantConfig.
		 *
		 * NOTE: flow-level serverCalls only fire when `itemType === 'flow'` is
		 * set on the manifest item. This flow does NOT set that flag, so this
		 * entry alone is NOT sufficient. The actual fix is to also place this
		 * call as a **node-level** serverCall on SupportingDocumentUpload (see
		 * nodes below).  Kept here for documentation / future-proofing.
		 */
		editTenantConfig: {
			errorMessage: '',
			directCall: resolveEditTenantConfig
		}
	},

	// ---------------------------------------------------------------------------
	// valuesToBigForm – populate the bigForm from the stored server-draft data
	// ---------------------------------------------------------------------------
	valuesToBigForm: (bf, store) => {
		// ------------------------------------------------------------------
		// STEP A  –  Synchronously seed `selected_company` + `tenant_document_config`
		//            from the summary item so that initFormFields on the start
		//            node can resolve the correct tenant config immediately.
		// ------------------------------------------------------------------
		store
			.select(getSelectedItemOne)
			.pipe(filter(Boolean), take(1))
			.subscribe((selectedItem: any) => {
				const tenantId = resolveTenantId(selectedItem);
				const localDraftTempKey = selectedItem?.tempKey ?? null;

				// selected_company
				if (!bf.bigForm.get('selected_company')) {
					bf.bigForm.addControl('selected_company', new UntypedFormControl(tenantId));
				} else {
					bf.bigForm.get('selected_company')?.setValue(tenantId);
				}

				if (!bf.bigForm.get('tempKey')) {
					bf.bigForm.addControl('tempKey', new UntypedFormControl(localDraftTempKey));
				} else {
					bf.bigForm.get('tempKey')?.setValue(localDraftTempKey);
				}

				// tenant_document_config (minimal – enough for getTenantConfig fallback)
				const tenantDocConfig = {
					company_id: tenantId,
					company_name: '',
					required_documents: [],
					metadata: {
						customer_detail_config: getEditCustomerDetailConfig(tenantId, null)
					},
					demand_creation: true,
					analyse_files_with_ai: false
				};
				if (!bf.bigForm.get('tenant_document_config')) {
					bf.bigForm.addControl('tenant_document_config', new UntypedFormControl(tenantDocConfig));
				} else {
					bf.bigForm.get('tenant_document_config')?.setValue(tenantDocConfig);
				}

				// Supplementary controls expected by CompanySelectionComponent / flow
				if (!bf.bigForm.get('selected_company_name')) {
					bf.bigForm.addControl('selected_company_name', new UntypedFormControl(null));
				}
				if (!bf.bigForm.get('selected_company_logo')) {
					bf.bigForm.addControl('selected_company_logo', new UntypedFormControl(null));
				}

				// Screen-visit flags – all true so tabs are available immediately
				const visitFlags = [
					'checked_company_selection',
					'checked_supporting_document_upload',
					'checked_customer_details',
					'checked_address_confirmation',
					'checked_job_details',
					'checked_appointments'
				];
				visitFlags.forEach(flag => {
					if (!bf.bigForm.get(flag)) {
						bf.bigForm.addControl(flag, new UntypedFormControl(true));
					} else {
						bf.bigForm.get(flag)?.setValue(true);
					}
				});

				// new_state – kept at 8 (draft) by default; Submit overrides at nav level
				if (!bf.bigForm.get('new_state')) {
					bf.bigForm.addControl('new_state', new UntypedFormControl(8));
				}

				// Map controls (with validators) – may already exist from PggCreateClaim spread
				const ensureMapControl = (name: string, value: any) => {
					if (bf.bigForm.get(name)) {
						bf.bigForm.get(name)?.setValidators([Validators.required]);
					} else {
						bf.addControl(name, new UntypedFormControl(value, [Validators.required]));
					}
				};
				ensureMapControl('latitude', null);
				ensureMapControl('longitude', null);
				ensureMapControl('province', null);
				ensureMapControl('area_code', null);
			});

		// ------------------------------------------------------------------
		// STEP B  –  Populate the full form from the complete draft item
		// ------------------------------------------------------------------
		store
			.select(getFullItemOne)
			.pipe(filter(Boolean), take(1))
			.subscribe((itemOne: any) => {
				const loan_information = itemOne?.loan_information ?? {};
				const address = loan_information?.address ?? {};
				const vouchers = asArray(loan_information?.vouchers);
				const rawDraftJobs = asArray(itemOne?.jobs);
				const location = itemOne?.location ?? {};
				const tenantId = resolveTenantId(itemOne);

				// Re-confirm selected_company from full item (in case summary had stale data)
				if (bf.bigForm.get('selected_company')) {
					bf.bigForm.get('selected_company').setValue(tenantId);
				} else {
					bf.bigForm.addControl('selected_company', new UntypedFormControl(tenantId));
				}

				if (!bf.bigForm.get('tempKey')) {
					bf.bigForm.addControl('tempKey', new UntypedFormControl(itemOne?.tempKey ?? null));
				} else {
					bf.bigForm.get('tempKey')?.setValue(itemOne?.tempKey ?? null);
				}

				// Also patch tenant_document_config.company_id so storesData/voucherTypesData
				// have the correct tenant_id even before the editTenantConfig serverCall resolves
				const existingTenantConfig = bf.bigForm.get('tenant_document_config')?.value;
				const editCustomerDetailConfig = getEditCustomerDetailConfig(tenantId, existingTenantConfig?.metadata ?? null);
				if (existingTenantConfig) {
					bf.bigForm.get('tenant_document_config').setValue({
						...existingTenantConfig,
						company_id: tenantId,
						metadata: {
							...(existingTenantConfig?.metadata ?? {}),
							customer_detail_config: editCustomerDetailConfig
						}
					});
				} else {
					bf.bigForm.addControl(
						'tenant_document_config',
						new UntypedFormControl({
							company_id: tenantId,
							required_documents: [],
							metadata: { customer_detail_config: editCustomerDetailConfig },
							demand_creation: true,
							analyse_files_with_ai: false
						})
					);
				}

				// --- Handler info ---
				store.select(getCurrentUser).pipe(take(1)).subscribe((user: any) => {
					const { full_name, contact_number } = user?.user ?? {};
					const email_address = user?.email ?? null;
					bf.patchValues({
						handler: loan_information?.handler ?? full_name ?? '',
						handlercontact: loan_information?.handlercontact ?? contact_number ?? '',
						handleremail: loan_information?.handleremail ?? email_address ?? ''
					});
				});

				// --- T&C acceptance (persisted in server draft payload) ---
				const savedTermsAcceptance = asBooleanFromApi(
					loan_information?.accept_terms_and_conditions ?? itemOne?.accept_terms_and_conditions
				);
				if (bf.bigForm.get('accept_terms_and_conditions')) {
					bf.bigForm.get('accept_terms_and_conditions')?.setValue(savedTermsAcceptance, { emitEvent: false });
				} else {
					bf.bigForm.addControl('accept_terms_and_conditions', new UntypedFormControl(savedTermsAcceptance));
				}

				// --- Map / address flat controls ---
				const parsedLocation = parseLocation(location);
				const lat = parsedLocation.lat ?? toNumberOrNull(address?.from_lat);
				const lng = parsedLocation.lng ?? toNumberOrNull(address?.from_lng);
				bf.bigForm.get('latitude')?.setValue(lat);
				bf.bigForm.get('longitude')?.setValue(lng);
				bf.bigForm.get('province')?.setValue(address?.claimprovince ?? null);
				bf.bigForm.get('area_code')?.setValue(address?.area_id ?? null);

				bf.patchValues({
					addressconfirmation: true,
					address_store_poi: address?.store_poi ?? null,
					address_searched: address?.address_searched ?? null,
					address_found: address?.address_found ?? null,
					address_from_lat: address?.from_lat ?? null,
					address_to_lat: address?.to_lat ?? null,
					address_from_lng: address?.from_lng ?? null,
					address_to_lng: address?.to_lng ?? null,
					address_suburb: address?.suburb ?? null
				});

				// --- Customer / Contact / Address form groups (tenant-aware) ---
				const tenantConfig = getEditCustomerDetailConfig(tenantId, bf.bigForm.get('tenant_document_config')?.value?.metadata ?? null);

				// client_details
				const clientDetailsGroup = bf.bigForm.get('client_details') as UntypedFormGroup;
				if (clientDetailsGroup) {
					// Group already exists (created by initFormFields) – patch values
					const patch: Record<string, any> = {};
					(tenantConfig?.customerDetails?.fields ?? []).forEach(field => {
						if (field.controlName) {
							patch[field.controlName] = resolveDraftValue(itemOne, field.storePath, '');
						}
					});
					clientDetailsGroup.patchValue(patch);
				} else {
					// Create the group from scratch (valuesToBigForm ran before initFormFields)
					const controls: Record<string, UntypedFormControl> = {};
					(tenantConfig?.customerDetails?.fields ?? []).forEach(field => {
						if (field.controlName) {
							const validators = field.validators ?? getValidators(field.controlName);
							controls[field.controlName] = new UntypedFormControl(
								resolveDraftValue(itemOne, field.storePath, ''),
								validators
							);
						}
					});
					bf.addControl('client_details', new UntypedFormGroup(controls));
				}

				// contact_details
				const contactDetailsGroup = bf.bigForm.get('contact_details') as UntypedFormGroup;
				if (contactDetailsGroup) {
					const patch: Record<string, any> = {};
					(tenantConfig?.contactDetails?.fields ?? []).forEach(field => {
						if (field.controlName) {
							patch[field.controlName] = resolveDraftValue(itemOne, field.storePath, '');
						}
					});
					contactDetailsGroup.patchValue(patch);
				} else {
					const controls: Record<string, UntypedFormControl> = {};
					(tenantConfig?.contactDetails?.fields ?? []).forEach(field => {
						if (field.controlName) {
							const validators = field.validators ?? getValidators(field.controlName);
							controls[field.controlName] = new UntypedFormControl(
								resolveDraftValue(itemOne, field.storePath, ''),
								validators
							);
						}
					});
					bf.addControl('contact_details', new UntypedFormGroup(controls));
				}

				// insured_address
				const insuredAddressGroup = bf.bigForm.get('insured_address') as UntypedFormGroup;
				const allAddressFields = [
					...(tenantConfig?.addressDetails?.commonFields ?? []),
					...(tenantConfig?.addressDetails?.houseEstateFields ?? []),
					...(tenantConfig?.addressDetails?.flatOfficeFields ?? [])
				];
				if (insuredAddressGroup) {
					const patch: Record<string, any> = {};
					allAddressFields.forEach(field => {
						if (field.controlName) {
							patch[field.controlName] = resolveDraftValue(itemOne, field.storePath, '');
						}
					});
					insuredAddressGroup.patchValue(patch);
				} else {
					const controls: Record<string, UntypedFormControl> = {};
					allAddressFields.forEach(field => {
						if (field.controlName) {
							const validators = field.validators ?? getValidators(field.controlName);
							controls[field.controlName] = new UntypedFormControl(
								resolveDraftValue(itemOne, field.storePath, ''),
								validators
							);
						}
					});
					bf.addControl('insured_address', new UntypedFormGroup(controls));
				}

				// --- Claim details ---
				bf.patchValues({
					claim_class: itemOne?.claim_type_id ?? itemOne?.claim_type ?? '',
					claim_class_description: loan_information?.claimtype ?? ''
				});
				if (!bf.bigForm.get('selectedClaimClass')) {
					bf.addControl('selectedClaimClass', new UntypedFormControl(null));
				}

				// --- Jobs / vouchers ---
				const createLocalDraftJob = (draftJob: any) => {
					const quantityValue = toNumberOrNull(draftJob?.quantity);
					const totalCostValue = toNumberOrNull(draftJob?.total_cost);
					const deliveryDistanceValue = toNumberOrNull(draftJob?.delivery_distance);
					const quantityAvailableValue = toNumberOrNull(draftJob?.quantity_available);
					const deliveryCostValue = toNumberOrNull(draftJob?.delivery_cost);
					const voucherTerms =
						typeof draftJob?.voucher_terms === 'object' && draftJob?.voucher_terms
							? draftJob.voucher_terms
							: typeof draftJob?.terms === 'string' && draftJob.terms.trim().length > 0
							? {
									inclusions: '',
									exclusions: '',
									full_text: draftJob.terms,
									voucher_type: draftJob?.title ?? draftJob?.voucher_name ?? loan_information?.voucher_type ?? '',
									id: null,
									version: ''
								}
							: null;

					return bf.fb.group({
						voucher_id: draftJob?.voucher_id ?? draftJob?.voucher_type_id ?? null,
						voucher_name: draftJob?.voucher_name ?? draftJob?.title ?? loan_information?.voucher_type ?? null,
						voucher_terms: voucherTerms,
						terms: draftJob?.terms ?? null,
						title: draftJob?.title ?? draftJob?.voucher_name ?? null,
						skill_id: toNumberOrNull(draftJob?.skill_id),
						total_cost: totalCostValue,
						delivery_distance: deliveryDistanceValue,
						quantity_available: quantityAvailableValue,
						quantity: [quantityValue ?? 1, [Validators.required, Validators.min(1)]],
						store_poi: [draftJob?.store_poi ?? null, [Validators.required]],
						what_is_being_installed: draftJob?.what_is_being_installed ?? null,
						collection_required: asBooleanFromApi(draftJob?.collection_required ?? draftJob?.collection),
						store_has_stock: asBooleanFromApi(draftJob?.store_has_stock ?? draftJob?.has_stock),
						delivery_cost: deliveryCostValue != null ? deliveryCostValue.toFixed(2) : '0.00',
						where_to_collect: draftJob?.where_to_collect ?? null,
						appointmentDatePicker: draftJob?.appointmentDatePicker ? new Date(draftJob.appointmentDatePicker) : null,
						appointmentTimePicker: draftJob?.appointmentTimePicker ?? null,
						appointmentTime: draftJob?.appointmentTime ?? draftJob?.appointment_type_id ?? null,
						range_start: draftJob?.range_start ?? null,
						range_end: draftJob?.range_end ?? null
					});
				};

				const createVoucherJob = (voucher: any) => {
					const appointment = normalizeAppointment(voucher?.appointment);
					const parsedVoucherId = Number(voucher?.voucher_type_id);
					const parsedSkillId = Number(voucher?.skill_id);
					const parsedQuantity = Number(voucher?.quantity);
					const voucherTerms =
						typeof voucher?.terms === 'string' && voucher.terms.trim().length > 0
							? {
									inclusions: '',
									exclusions: '',
									full_text: voucher.terms,
									voucher_type: voucher?.title ?? loan_information?.voucher_type ?? '',
									id: null,
									version: ''
								}
							: null;
					const timePicker = appointment?.requestedappointmenttime
						? {
								hour: String(appointment.requestedappointmenttime).split(':')[0] ?? '',
								minutes: String(appointment.requestedappointmenttime).split(':')[1] ?? ''
						  }
						: null;

					return bf.fb.group({
						voucher_id: Number.isFinite(parsedVoucherId) ? parsedVoucherId : voucher?.voucher_type_id ?? null,
						voucher_name: loan_information?.voucher_type ?? voucher?.title ?? null,
						voucher_terms: voucherTerms,
						terms: voucher?.terms ?? null,
						title: voucher?.title ?? null,
						skill_id: Number.isFinite(parsedSkillId) ? parsedSkillId : voucher?.skill_id ?? null,
						total_cost: voucher?.total_cost ?? null,
						delivery_distance: voucher?.delivery_distance ?? null,
						quantity_available: voucher?.quantity_available ?? null,
						quantity: [Number.isFinite(parsedQuantity) ? parsedQuantity : voucher?.quantity ?? 1, [Validators.required, Validators.min(1)]],
						store_poi: [voucher?.store_poi ?? null, [Validators.required]],
						what_is_being_installed: voucher?.what_is_being_installed ?? null,
						collection_required: asBooleanFromApi(voucher?.collection),
						store_has_stock: asBooleanFromApi(voucher?.has_stock),
						delivery_cost: voucher?.delivery_cost != null ? Number(voucher.delivery_cost).toFixed(2) : '0.00',
						where_to_collect: null,
						appointmentDatePicker: appointment?.requestedappointmentdate
							? new Date(appointment.requestedappointmentdate)
							: null,
						appointmentTimePicker: timePicker,
						appointmentTime: appointment?.appointment_type_id ?? null,
						range_start: appointment?.from_time ?? null,
						range_end: appointment?.to_time ?? null
					});
				};

				const defaultJob = () =>
					bf.fb.group({
						voucher_id: null,
						voucher_name: null,
						voucher_terms: null,
						terms: null,
						title: null,
						skill_id: null,
						total_cost: null,
						delivery_distance: null,
						quantity_available: null,
						quantity: [1, [Validators.required, Validators.min(1)]],
						store_poi: [null, [Validators.required]],
						what_is_being_installed: null,
						collection_required: false,
						store_has_stock: false,
						delivery_cost: '0.00',
						where_to_collect: null,
						appointmentDatePicker: null,
						appointmentTimePicker: null,
						appointmentTime: null,
						range_start: null,
						range_end: null
					});

				const jobArray =
					rawDraftJobs.length > 0
						? rawDraftJobs.map(job => createLocalDraftJob(job))
						: vouchers.length > 0
						? vouchers.map(v => createVoucherJob(v))
						: [defaultJob()];

				// If jobs already exist (from initFormFields), replace with draft data
				if (bf.bigForm.get('jobs')) {
					bf.bigForm.removeControl('jobs');
				}
				bf.bigForm.addControl('jobs', new UntypedFormArray(jobArray));

				// Ensure T&C text is available even when user jumps straight to TermsAndConditions tab.
				const firstJobGroup = (bf.bigForm.get('jobs') as UntypedFormArray)?.at(0) as UntypedFormGroup | null;
				const firstVoucherTerms = firstJobGroup?.get('voucher_terms')?.value;
				const firstVoucherFullText =
					firstVoucherTerms?.full_text ??
					(firstVoucherTerms && typeof firstVoucherTerms === 'string' ? firstVoucherTerms : null) ??
					firstJobGroup?.get('terms')?.value ??
					null;
				if (firstVoucherFullText) {
					if (bf.bigForm.get('terms_and_conditions')) {
						bf.bigForm.get('terms_and_conditions')?.setValue(`Full Terms and Conditions\r\n${firstVoucherFullText}`, {
							emitEvent: false
						});
					} else {
						bf.bigForm.addControl(
							'terms_and_conditions',
							new UntypedFormControl(`Full Terms and Conditions\r\n${firstVoucherFullText}`)
						);
					}
				}

				// Restore additional identity controls from draft voucher data
				const additionalIdentities = bf.bigForm.get('tenant_document_config')?.value?.metadata?.additional_identities ?? [];
				if (additionalIdentities.length > 0) {
					const voucherArray = vouchers;
					jobArray.forEach((jobGroup: UntypedFormGroup, index: number) => {
						const savedVoucher = voucherArray[index];
						const savedIdentities = asArray(savedVoucher?.additional_identities ?? loan_information?.additional_identities);
						additionalIdentities.forEach((identity: any) => {
							const controlName = `identity_${identity.identity_type}`;
							const savedValue =
								savedIdentities.find((si: any) => si.identity_type === identity.identity_type)?.value ??
								savedIdentities.find((si: any) => si.identity_type === identity.identity_type)?.identity ??
								null;
							if (!jobGroup.get(controlName)) {
								jobGroup.addControl(controlName, new UntypedFormControl(savedValue, [Validators.required]));
							} else {
								jobGroup.get(controlName)?.setValue(savedValue);
							}
						});
					});
				}

				// --- Appointment data ---
				bf.patchValues({
					on_site_contact: loan_information?.onsitecontact ?? '',
					on_site_notes: loan_information?.onsitenotes ?? '',
					on_site_person: loan_information?.onsiteperson ?? ''
				});

				// --- What matters ---
				bf.patchValues({
					whatmatters_from_voucher: loan_information?.whatmatters ?? ''
				});
			});
	},

	// ---------------------------------------------------------------------------
	// nodes – reference the correct nodes from PggCreateClaim with edit-specific
	//         navigation overrides
	// ---------------------------------------------------------------------------
	nodes: {
		// CustomerDetails inherits fully from PggCreateClaim (component, initFormFields, etc.)
		CustomerDetails: {
			...PggCreateClaim.nodes.CustomerDetails
		},

		AddressConfirmation: {
			...PggCreateClaim.nodes.AddressConfirmation
		},

		JobDetails: {
			...PggCreateClaim.nodes.JobDetails,
			// Override serverCalls so voucherTypesData & storesData wait for a
			// valid tenant_id rather than reading undefined from bigForm.
			// In the create flow selected_company is set synchronously by
			// CompanySelectionComponent, but in the edit flow it arrives
			// asynchronously from getFullItemOne.
			serverCalls: {
				...PggCreateClaim.nodes.JobDetails.serverCalls,

				voucherTypesData: {
					errorMessage: '',
					directCall: (http: any, store: any, sq: any, bf: any) => {
						const itemOneFromForm = bf.bigForm.get('itemOne')?.value ?? null;
						const jobsFromForm = asArray(bf.bigForm.get('jobs')?.value);
						const draftVoucherFallbacks = jobsFromForm
							.filter((job: any) => !!(job?.voucher_id ?? job?.voucher_type_id))
							.map((job: any) => ({
								id: job?.voucher_id ?? job?.voucher_type_id,
								name: job?.voucher_name ?? job?.title ?? itemOneFromForm?.loan_information?.voucher_type ?? 'Draft voucher',
								description: job?.voucher_description ?? null,
								total_value: job?.total_cost ?? null,
								client_value: bf.bigForm.get('voucher_client_value')?.value ?? null,
								sp_value: bf.bigForm.get('voucher_sp_value')?.value ?? null,
								terms_and_conds:
									job?.voucher_terms ??
									(job?.terms
										? {
												inclusions: '',
												exclusions: '',
												full_text: job.terms,
												voucher_type: job?.voucher_name ?? job?.title ?? itemOneFromForm?.loan_information?.voucher_type ?? '',
												id: null,
												version: ''
										  }
										: null),
								voucher_type: {
									id: job?.voucher_type_id ?? job?.voucher_id ?? null,
									claim_type: bf.bigForm.get('claim_class')?.value ?? itemOneFromForm?.claim_type_id ?? null,
									skill: job?.skill_id ?? null
								},
								product_template: {
									qty: job?.quantity_available ?? null,
									mid: '',
									sku: '',
									meta_data: {
										delivery_distance: job?.delivery_distance ?? null
									}
								}
							}));
						const mergeDraftFallbacks = (voucherTypes: any[] = []) => {
							const existingIds = new Set((voucherTypes ?? []).map((voucher: any) => String(voucher?.id)));
							const missingDraftVouchers = draftVoucherFallbacks.filter((voucher: any) => !existingIds.has(String(voucher?.id)));
							return [...missingDraftVouchers, ...(voucherTypes ?? [])];
						};
						void sq;
						const tenantIdFromItemOne = resolveTenantId(itemOneFromForm) ?? Number(bf.bigForm.get('selected_company')?.value ?? null);
						if (!tenantIdFromItemOne) return of(mergeDraftFallbacks());

						// Keep selected_company aligned for other node calls.
						if (bf.bigForm.get('selected_company')) {
							bf.bigForm.get('selected_company').setValue(tenantIdFromItemOne);
						} else {
							bf.bigForm.addControl('selected_company', new UntypedFormControl(tenantIdFromItemOne));
						}

						if (!window.navigator.onLine) {
							return of(mergeDraftFallbacks());
						}

						return http.get(`${environment.api_url}v1/voucher_type_action/get_voucher_types/?tenant_id=${tenantIdFromItemOne}`).pipe(
							tap(console.log),
							take(1),
							map((res: any) => (res.success == true ? mergeDraftFallbacks(res.payload?.voucher_types) : mergeDraftFallbacks())),
							catchError(() => of(mergeDraftFallbacks()))
						);
					}
				},

				storesData: {
					errorMessage: '',
					directCall: (http: any, store: any, sq: any, bf: any) => {
						const selectedCompanyCtrl = bf.bigForm.get('selected_company');
						const tenantDocCtrl = bf.bigForm.get('tenant_document_config');

						return merge(
							of(Number(tenantDocCtrl?.value?.company_id ?? selectedCompanyCtrl?.value ?? null)),
							tenantDocCtrl
								? tenantDocCtrl.valueChanges.pipe(
										startWith(tenantDocCtrl.value),
										map((cfg: any) => Number(cfg?.company_id ?? null))
								  )
								: of(null),
							selectedCompanyCtrl
								? selectedCompanyCtrl.valueChanges.pipe(
										startWith(selectedCompanyCtrl.value),
										map((v: any) => Number(v ?? null))
								  )
								: of(null),
							store.select(getFullItemOne).pipe(
								filter(Boolean),
								map((itemOne: any) =>
									Number(itemOne?.tenant_id ?? itemOne?.claim?.tenant_id ?? itemOne?.loan_information?.tenant_id ?? null)
								)
							)
						).pipe(
							filter((tid: any) => Number.isFinite(tid) && tid > 0),
							take(1),
							tap((tid: number) => {
								if (selectedCompanyCtrl) selectedCompanyCtrl.setValue(tid);
								if (tenantDocCtrl) {
									const existingConfig = tenantDocCtrl.value ?? {};
									tenantDocCtrl.setValue({ ...existingConfig, company_id: tid });
								}
							}),
							switchMap((tid: number) =>
								http.get(`${environment.api_url}v1/poi_action/get_stores?tenant_id=${tid}`).pipe(
									take(1),
									map((res: any) => res?.payload ?? []),
									map((stores: any[]) => {
										return (stores ?? []).map((s: any) => {
											const rawDisplay = s?.name ?? s?.poi_type_name ?? s?.description ?? '';
											// Insert a dash between the tenant prefix and the store name (e.g. "Makro Wonderpark" -> "Makro - Wonderpark")
											// To strip the prefix entirely instead, use: rawDisplay.replace(/^makro\s*/i, '').trim()
											const display = rawDisplay.replace(/^(makro)\s+/i, '$1 - ');
											const value = s?.id ?? s?.mid ?? s?.code ?? display ?? '';
											return { value, display, location: s?.location ?? null };
										});
									})
								)
							)
						);
					}
				}
			}
		},

		ClaimAppointments: {
			...PggCreateClaim.nodes.ClaimAppointments,
			// Edit flow should still pass through documents and T&C before submit.
			navs: [
				// whatMattersNav,
				{ text: 'Continue', color: 'primary', nextNode: 'SupportingDocumentUpload' }
			]
		},

		// SupportingDocumentUpload should route forward into T&C (never loop back to CustomerDetails).
		// Node-level serverCall resolves the tenant config (required_documents) so the
		// component displays the correct document upload cards instead of
		// "No documents required for this company."
		SupportingDocumentUpload: {
			...PggCreateClaim.nodes.SupportingDocumentUpload,
			serverCalls: {
				editTenantConfig: {
					errorMessage: '',
					directCall: resolveEditTenantConfig
				}
			},
			navs: [
				// whatMattersNav,
				{ text: 'Continue', color: 'primary', nextNode: 'TermsAndConditions' }
			]
		},

		TermsAndConditions: {
			...PggCreateClaim.nodes.TermsAndConditions,
			navs: [
				{
					text: 'Continue',
					color: 'primary',
					nextNode: 'ClaimSubmissionSummary'
				}
			]
		},

		ClaimSubmissionSummary: {
			...PggCreateClaim.nodes.ClaimSubmissionSummary,
			initFormFields: (bf, ...args) => {
				// Edit flow: set new_state to 138 on submit screen so mapper/submissionData sends 138 on Submit.
				const newStateControl = bf.bigForm.get('new_state');
				if (newStateControl) {
					newStateControl.setValue(138, { emitEvent: false });
				} else {
					bf.bigForm.addControl('new_state', new UntypedFormControl(138));
				}
				const base = PggCreateClaim.nodes.ClaimSubmissionSummary.initFormFields;
				return typeof base === 'function' ? base(bf, ...args) : undefined;
			},
			navs: [
				// whatMattersNav,
				{
					text: 'Update Draft',
					nextNode: 'DraftSubmissionSuccess',
					serverFirst: true,
					serverCalls: {
						saveAsServerDraft: {
							errorMessage: 'Could not update the draft!',
							serviceVariable: 'pggService',
							functionName: 'updateEditedDraft',
							followUpSuccessCalls: {
								deleteCurrentEditableClaim: {
									errorMessage: 'Could not delete the current editable claim!',
									serviceVariable: 'pggService',
									functionName: 'deleteCurrentEditableItem'
								}
							}
						}
					}
				},
				{
					text: 'Submit',
					linkType: 'submit',
					optIntoValidation: true,
					color: 'primary',
					nextNode: 'SubmissionSuccess',
					serverFirst: true,
					serverCalls: {
						response: {
							errorMessage: 'This claim could not be submitted!',
							serviceVariable: 'pggService',
							functionName: 'submitEditedClaimWithOfflineFallback'
						}
					}
				}
			]
		},

		SubmissionSuccess: {
			...PggCreateClaim.nodes.SubmissionSuccess,
			// Remove "New Voucher" nav since CompanySelection isn't in this flow.
			// The FLXSuccessTickComponent's "Continue" button handles navigation to workflow.
			navs: []
		},
		DraftSubmissionSuccess: {
			...PggCreateClaim.nodes.SubmissionSuccess,
			inputs: {
				...(PggCreateClaim.nodes.SubmissionSuccess as any)?.inputs,
				heading: 'Draft saved successfully. You can return to the workflow.'
			},
			// Keep edit-flow behavior aligned: no "New Voucher" nav.
			navs: []
		}
	}
};
